'use client';

import { Move } from 'lucide-react';
import type { TextBlock } from '@/features/drawing/model';
import type { ResizeHandle } from './types';

interface ResizeHandlesProps {
  blockId: string;
  zoom: number;
  textBlocks: TextBlock[];
  onDragStart: (e: React.MouseEvent, block: TextBlock) => void;
  onTouchDragStart: (e: React.TouchEvent, block: TextBlock) => void;
  onResizeStart: (
    e: React.MouseEvent,
    blockId: string,
    handle: ResizeHandle
  ) => void;
  onTouchResizeStart: (
    e: React.TouchEvent,
    blockId: string,
    handle: ResizeHandle
  ) => void;
}

export function ResizeHandles({
  blockId,
  zoom,
  textBlocks,
  onDragStart,
  onTouchDragStart,
  onResizeStart,
  onTouchResizeStart,
}: ResizeHandlesProps) {
  // Only corner handles (exclude 'ne' because delete button is there)
  const handles: ResizeHandle[] = ['nw', 'se', 'sw'];

  // Larger handles for mobile (easier to tap)
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  // Handle sizes are constant on screen, so divide by zoom
  const handleSizePx = isMobile ? 16 : 10;
  const handleOffsetPx = isMobile ? -8 : -5;
  const handleSize = handleSizePx / zoom;
  const handleOffset = handleOffsetPx / zoom;
  const handleBorderWidth = 1.5 / zoom;

  return (
    <>
      {handles.map((handle) => {
        // Special styling for 'nw' - it's the move handle with icon
        const isMoveHandle = handle === 'nw';

        let style: React.CSSProperties = {
          position: 'absolute',
          width: isMoveHandle
            ? `${handleSize + 8 / zoom}px`
            : `${handleSize}px`,
          height: isMoveHandle
            ? `${handleSize + 8 / zoom}px`
            : `${handleSize}px`,
          backgroundColor: '#14b8a6',
          border: `${handleBorderWidth}px solid white`,
          borderRadius: isMoveHandle ? `${4 / zoom}px` : '50%',
          cursor: isMoveHandle ? 'move' : `${handle}-resize`,
          boxShadow: `0 ${1 / zoom}px ${2 / zoom}px rgba(0,0,0,0.2)`,
          touchAction: 'none',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          WebkitTapHighlightColor: 'transparent',
          userSelect: 'none',
          WebkitUserSelect: 'none',
        };

        switch (handle) {
          case 'nw':
            style = {
              ...style,
              top: `${handleOffset - 2 / zoom}px`,
              left: `${handleOffset - 2 / zoom}px`,
            };
            break;
          case 'se':
            style = {
              ...style,
              bottom: `${handleOffset}px`,
              right: `${handleOffset}px`,
            };
            break;
          case 'sw':
            style = {
              ...style,
              bottom: `${handleOffset}px`,
              left: `${handleOffset}px`,
            };
            break;
        }

        return (
          <div
            key={handle}
            style={style}
            onMouseDown={(e) => {
              if (isMoveHandle) {
                const block = textBlocks.find((b) => b.id === blockId);
                if (block) {
                  onDragStart(e, block);
                }
              } else {
                onResizeStart(e, blockId, handle);
              }
            }}
            onTouchStart={(e) => {
              if (isMoveHandle) {
                const block = textBlocks.find((b) => b.id === blockId);
                if (block) {
                  onTouchDragStart(e, block);
                }
              } else {
                onTouchResizeStart(e, blockId, handle);
              }
            }}
          >
            {isMoveHandle && (
              <Move
                size={(isMobile ? 14 : 12) / zoom}
                strokeWidth={2.5 / zoom}
                color="white"
                style={{ pointerEvents: 'none' }}
              />
            )}
          </div>
        );
      })}
    </>
  );
}
